import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Hotel, ArrowLeft, Calendar, MapPin, Users, Phone, Mail, CheckCircle, XCircle, AlertCircle, Loader2, FileCheck2, UserCheck, Hash, ClipboardCheck, BedDouble, Download } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import DashboardLayout from "../components/layouts/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending_availability: { label: "Pending Availability", color: "bg-amber-100 text-amber-700 border-amber-200" },
  available: { label: "Available — Confirm Booking", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  not_available: { label: "Not Available", color: "bg-rose-100 text-rose-700 border-rose-200" },
  guest_confirmed: { label: "Guest Confirmed — Awaiting Hotel", color: "bg-violet-100 text-violet-700 border-violet-200" },
  hotel_confirmed: { label: "Hotel Confirmed", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  completed: { label: "Completed", color: "bg-blue-100 text-blue-700 border-blue-200" },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-600 border-gray-200" },
};

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function HotelRequestDetail({ type = "agent" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [hr, setHr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Admin: mark available form (with hotel_name/room_type override + rates)
  const [availOpen, setAvailOpen] = useState(false);
  const [availForm, setAvailForm] = useState({
    hotel_name: "",
    room_type: "",
    room_rate_per_night: "",
    extra_adult_rate: "",
    cwb_rate: "",
    cnb_rate: "",
    admin_note: "",
  });

  // Agent/Admin: edit request form
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Admin: mark not available
  const [notAvailOpen, setNotAvailOpen] = useState(false);
  const [notAvailForm, setNotAvailForm] = useState({ admin_note: "" });

  // Agent: confirm guest
  const [guestOpen, setGuestOpen] = useState(false);
  const [guestForm, setGuestForm] = useState({ guest_name: "", guest_mobile: "", guest_email: "" });

  // Admin: confirm hotel
  const [hotelConfirmOpen, setHotelConfirmOpen] = useState(false);
  const [hotelConfirmForm, setHotelConfirmForm] = useState({ hotel_confirmation_number: "", admin_note: "" });

  // Cancel
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const isAdmin = user?.role === "admin";

  const fetchHr = async () => {
    try {
      const res = await axios.get(`${API}/hotel-requests/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setHr(res.data);
      // Prefill availability form with current hotel info + saved rates
      setAvailForm({
        hotel_name: res.data?.hotel_name || "",
        room_type: res.data?.room_type || "",
        room_rate_per_night: res.data?.room_rate_per_night || "",
        extra_adult_rate: res.data?.extra_adult_rate || "",
        cwb_rate: res.data?.cwb_rate || "",
        cnb_rate: res.data?.cnb_rate || "",
        admin_note: res.data?.admin_availability_note || "",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load request");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHr(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const callAction = async (path, body, successMsg) => {
    try {
      setBusy(true);
      const res = await axios.put(`${API}/hotel-requests/${id}/${path}`, body, { headers: { Authorization: `Bearer ${token}` } });
      setHr(res.data);
      toast.success(successMsg);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Auto-computed pricing breakdown (live as admin types)
  const pricing = (() => {
    if (!hr) return null;
    const nights = Number(hr.nights || 0);
    const rooms = Number(hr.rooms || 0);
    const adults = Number(hr.pax_adults || 0);
    const cwb = Number(hr.cwb || 0);
    const cnb = Number(hr.cnb || 0);
    const extraAdults = Math.max(0, adults - 2 * rooms); // 2 adults/room is std occupancy
    const rrate = parseFloat(availForm.room_rate_per_night || 0) || 0;
    const eaRate = parseFloat(availForm.extra_adult_rate || 0) || 0;
    const cwbRate = parseFloat(availForm.cwb_rate || 0) || 0;
    const cnbRate = parseFloat(availForm.cnb_rate || 0) || 0;
    const roomTotal = rrate * rooms * nights;
    const extraAdultTotal = eaRate * extraAdults * nights;
    const cwbTotal = cwbRate * cwb * nights;
    const cnbTotal = cnbRate * cnb * nights;
    const total = roomTotal + extraAdultTotal + cwbTotal + cnbTotal;
    return { nights, rooms, adults, cwb, cnb, extraAdults, rrate, eaRate, cwbRate, cnbRate, roomTotal, extraAdultTotal, cwbTotal, cnbTotal, total };
  })();

  const submitAvailable = async () => {
    if (!pricing || pricing.total <= 0) {
      toast.error("Please enter Per-Room/Night rate (Total must be greater than 0)");
      return;
    }
    const ok = await callAction("mark-available", {
      hotel_name: availForm.hotel_name || hr.hotel_name || "",
      room_type: availForm.room_type || "",
      room_rate_per_night: pricing.rrate,
      extra_adult_rate: pricing.eaRate,
      cwb_rate: pricing.cwbRate,
      cnb_rate: pricing.cnbRate,
      extra_adults: pricing.extraAdults,
      total_amount: pricing.total,
      admin_note: availForm.admin_note || "",
    }, "Marked available — agent notified by email");
    if (ok) setAvailOpen(false);
  };

  const openEdit = () => {
    setEditForm({
      hotel_name: hr.hotel_name || "",
      city: hr.city || "",
      destination: hr.destination || "",
      check_in: hr.check_in || "",
      check_out: hr.check_out || "",
      rooms: hr.rooms || 1,
      room_type: hr.room_type || "",
      meal_plan: hr.meal_plan || "CP",
      pax_adults: hr.pax_adults || 1,
      pax_children: hr.pax_children || 0,
      cwb: hr.cwb || 0,
      cnb: hr.cnb || 0,
      special_requests: hr.special_requests || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editForm.city?.trim() || !editForm.check_in || !editForm.check_out) {
      toast.error("City, check-in and check-out are required");
      return;
    }
    if (new Date(editForm.check_out) <= new Date(editForm.check_in)) {
      toast.error("Check-out must be after check-in");
      return;
    }
    try {
      setBusy(true);
      const payload = {
        ...editForm,
        rooms: parseInt(editForm.rooms || 1, 10),
        pax_adults: parseInt(editForm.pax_adults || 1, 10),
        pax_children: parseInt(editForm.pax_children || 0, 10),
        cwb: parseInt(editForm.cwb || 0, 10),
        cnb: parseInt(editForm.cnb || 0, 10),
      };
      const res = await axios.put(`${API}/hotel-requests/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setHr(res.data);
      toast.success("Request updated");
      setEditOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update request");
    } finally {
      setBusy(false);
    }
  };

  const submitNotAvailable = async () => {
    const ok = await callAction("mark-not-available", notAvailForm, "Marked not available — agent notified");
    if (ok) setNotAvailOpen(false);
  };

  const submitGuest = async () => {
    if (!guestForm.guest_name.trim() || !guestForm.guest_mobile.trim()) {
      toast.error("Guest name and mobile are required");
      return;
    }
    const ok = await callAction("confirm-guest", guestForm, "Booking confirmed — admin notified");
    if (ok) setGuestOpen(false);
  };

  const submitHotelConfirm = async () => {
    if (!hotelConfirmForm.hotel_confirmation_number.trim()) {
      toast.error("Hotel confirmation number is required");
      return;
    }
    const ok = await callAction("confirm-hotel", hotelConfirmForm, "Hotel confirmed — agent notified by email");
    if (ok) setHotelConfirmOpen(false);
  };

  const submitCancel = async () => {
    const ok = await callAction("cancel", { reason: cancelReason }, "Request cancelled");
    if (ok) setCancelOpen(false);
  };

  const submitComplete = async () => {
    await callAction("complete", {}, "Marked as completed — cleared from the pending Hotel Requests count");
  };

  const downloadVoucher = async () => {
    try {
      setBusy(true);
      const res = await axios.get(`${API}/hotel-requests/${id}/voucher`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Hotel-Voucher-${hr.request_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Voucher downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download voucher");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <DashboardLayout type={type}><div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div></DashboardLayout>;
  }
  if (!hr) {
    return <DashboardLayout type={type}><div className="p-8 text-center text-gray-500">Request not found</div></DashboardLayout>;
  }

  const meta = STATUS_META[hr.status] || STATUS_META.pending_availability;
  const backHref = type === "admin" ? "/admin/hotels" : "/agent/hotels";

  return (
    <DashboardLayout type={type}>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="hotel-request-detail">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-emerald-700 font-bold bg-emerald-50 px-3 py-1.5 rounded-lg text-base">{hr.request_number}</span>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${meta.color}`}>{meta.label}</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Hotel className="w-7 h-7 text-emerald-600" /> {hr.hotel_name}</h1>
            <p className="text-gray-500 text-sm flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> {hr.city}{hr.destination ? ` · ${hr.destination}` : ""}</p>
          </div>
          <Button variant="outline" onClick={() => navigate(backHref)} data-testid="hotel-detail-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main */}
          <div className="lg:col-span-2 space-y-6">
            {/* Trip Details */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="w-5 h-5 text-emerald-600" /> Stay Details</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div><p className="text-gray-500 text-xs">Check-in</p><p className="font-semibold text-gray-900">{hr.check_in}</p></div>
                <div><p className="text-gray-500 text-xs">Check-out</p><p className="font-semibold text-gray-900">{hr.check_out}</p></div>
                <div><p className="text-gray-500 text-xs">Nights</p><p className="font-semibold text-gray-900">{hr.nights}</p></div>
                <div><p className="text-gray-500 text-xs">Rooms</p><p className="font-semibold text-gray-900">{hr.rooms}</p></div>
                <div><p className="text-gray-500 text-xs">Room Type</p><p className="font-semibold text-gray-900">{hr.room_type || "—"}</p></div>
                <div><p className="text-gray-500 text-xs">Meal Plan</p><p className="font-semibold text-gray-900">{hr.meal_plan || "—"}</p></div>
                <div><p className="text-gray-500 text-xs">Pax</p><p className="font-semibold text-gray-900">{hr.pax_adults}A{hr.pax_children > 0 ? ` + ${hr.pax_children}C` : ""}</p></div>
                <div>
                  <p className="text-gray-500 text-xs">CWB / CNB</p>
                  <p className="font-semibold text-gray-900">{hr.cwb || 0} / {hr.cnb || 0}</p>
                </div>
                {Array.isArray(hr.children_ages) && hr.children_ages.length > 0 && (
                  <div className="col-span-2 sm:col-span-4">
                    <p className="text-gray-500 text-xs">Children Ages</p>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {hr.children_ages.map((age, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200" data-testid={`child-age-${i}`}>
                          Child {i + 1}: {age} yr{age === 1 ? "" : "s"}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {hr.special_requests && (
                  <div className="col-span-2 sm:col-span-4"><p className="text-gray-500 text-xs">Special Requests</p><p className="text-gray-800">{hr.special_requests}</p></div>
                )}
              </CardContent>
            </Card>

            {/* Agent info (admin view) */}
            {isAdmin && (
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-5 h-5 text-violet-600" /> Agent</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div><p className="text-gray-500 text-xs">Company</p><p className="font-semibold text-gray-900">{hr.agent_company || "—"}</p></div>
                  <div><p className="text-gray-500 text-xs">Name</p><p className="font-semibold text-gray-900">{hr.agent_name || "—"}</p></div>
                  <div><p className="text-gray-500 text-xs">Email</p><p className="font-semibold text-gray-900">{hr.agent_email || "—"}</p></div>
                </CardContent>
              </Card>
            )}

            {/* Availability info */}
            {hr.admin_availability_at && (
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileCheck2 className="w-5 h-5 text-emerald-600" /> Availability Response</CardTitle></CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {hr.status !== "not_available" && hr.total_amount > 0 && (
                    <div className="rounded-lg border border-gray-200 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr className="text-[10px] uppercase tracking-wide text-gray-500">
                            <th className="px-3 py-2 text-left">Line Item</th>
                            <th className="px-3 py-2 text-right">Rate</th>
                            <th className="px-3 py-2 text-center">Qty × Nights</th>
                            <th className="px-3 py-2 text-right">Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hr.room_rate_per_night > 0 && (
                            <tr className="border-t border-gray-100">
                              <td className="px-3 py-2 text-gray-900">Per Room / Night</td>
                              <td className="px-3 py-2 text-right tabular-nums">{formatINR(hr.room_rate_per_night)}</td>
                              <td className="px-3 py-2 text-center text-gray-500 text-xs">{hr.rooms} × {hr.nights}N</td>
                              <td className="px-3 py-2 text-right tabular-nums font-medium">{formatINR((hr.room_rate_per_night || 0) * (hr.rooms || 0) * (hr.nights || 0))}</td>
                            </tr>
                          )}
                          {hr.extra_adult_rate > 0 && hr.extra_adults > 0 && (
                            <tr className="border-t border-gray-100">
                              <td className="px-3 py-2 text-gray-900">Extra Adult</td>
                              <td className="px-3 py-2 text-right tabular-nums">{formatINR(hr.extra_adult_rate)}</td>
                              <td className="px-3 py-2 text-center text-gray-500 text-xs">{hr.extra_adults} × {hr.nights}N</td>
                              <td className="px-3 py-2 text-right tabular-nums font-medium">{formatINR((hr.extra_adult_rate || 0) * (hr.extra_adults || 0) * (hr.nights || 0))}</td>
                            </tr>
                          )}
                          {hr.cwb_rate > 0 && hr.cwb > 0 && (
                            <tr className="border-t border-gray-100">
                              <td className="px-3 py-2 text-gray-900">CWB</td>
                              <td className="px-3 py-2 text-right tabular-nums">{formatINR(hr.cwb_rate)}</td>
                              <td className="px-3 py-2 text-center text-gray-500 text-xs">{hr.cwb} × {hr.nights}N</td>
                              <td className="px-3 py-2 text-right tabular-nums font-medium">{formatINR((hr.cwb_rate || 0) * (hr.cwb || 0) * (hr.nights || 0))}</td>
                            </tr>
                          )}
                          {hr.cnb_rate > 0 && hr.cnb > 0 && (
                            <tr className="border-t border-gray-100">
                              <td className="px-3 py-2 text-gray-900">CNB</td>
                              <td className="px-3 py-2 text-right tabular-nums">{formatINR(hr.cnb_rate)}</td>
                              <td className="px-3 py-2 text-center text-gray-500 text-xs">{hr.cnb} × {hr.nights}N</td>
                              <td className="px-3 py-2 text-right tabular-nums font-medium">{formatINR((hr.cnb_rate || 0) * (hr.cnb || 0) * (hr.nights || 0))}</td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot className="bg-emerald-50 border-t-2 border-emerald-300">
                          <tr>
                            <td colSpan={3} className="px-3 py-3 text-right font-semibold text-emerald-900 uppercase text-xs tracking-wide">Total Amount</td>
                            <td className="px-3 py-3 text-right tabular-nums font-bold text-emerald-700 text-base">{formatINR(hr.total_amount)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                  {hr.admin_availability_note && (
                    <div className={`p-3 rounded-md text-sm ${hr.status === "not_available" ? "bg-rose-50 text-rose-800" : "bg-emerald-50 text-emerald-800"}`}>
                      {hr.admin_availability_note}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Guest info */}
            {hr.guest_name && (
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><UserCheck className="w-5 h-5 text-violet-600" /> Guest Information</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div><p className="text-gray-500 text-xs">Name</p><p className="font-semibold text-gray-900">{hr.guest_name}</p></div>
                  <div><p className="text-gray-500 text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> Mobile</p><p className="font-semibold text-gray-900">{hr.guest_mobile}</p></div>
                  <div><p className="text-gray-500 text-xs flex items-center gap-1"><Mail className="w-3 h-3" /> Email</p><p className="font-semibold text-gray-900">{hr.guest_email || "—"}</p></div>
                </CardContent>
              </Card>
            )}

            {/* Hotel confirmation */}
            {hr.hotel_confirmation_number && (
              <Card className="bg-emerald-50 border-emerald-200 shadow-sm">
                <CardHeader><CardTitle className="text-base flex items-center gap-2 text-emerald-800"><ClipboardCheck className="w-5 h-5" /> Hotel Confirmation</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex items-center gap-2"><Hash className="w-4 h-4 text-emerald-700" /><p className="font-mono font-bold text-emerald-900 text-lg">{hr.hotel_confirmation_number}</p></div>
                  {hr.admin_confirmation_note && <p className="text-emerald-800">{hr.admin_confirmation_note}</p>}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Actions sidebar */}
          <div className="space-y-4">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader><CardTitle className="text-base">Actions</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {isAdmin && hr.status === "pending_availability" && (
                  <>
                    <Button onClick={() => setAvailOpen(true)} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="admin-mark-available-btn">
                      <CheckCircle className="w-4 h-4" /> Mark Available
                    </Button>
                    <Button onClick={() => setNotAvailOpen(true)} disabled={busy} variant="outline" className="w-full gap-2 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="admin-mark-not-available-btn">
                      <XCircle className="w-4 h-4" /> Mark Not Available
                    </Button>
                  </>
                )}
                {!isAdmin && hr.status === "available" && (
                  <Button onClick={() => setGuestOpen(true)} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="agent-confirm-guest-btn">
                    <UserCheck className="w-4 h-4" /> Confirm Booking with Guest Details
                  </Button>
                )}
                {isAdmin && hr.status === "guest_confirmed" && (
                  <Button onClick={() => setHotelConfirmOpen(true)} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="admin-confirm-hotel-btn">
                    <ClipboardCheck className="w-4 h-4" /> Enter Hotel Confirmation #
                  </Button>
                )}
                {!["cancelled", "hotel_confirmed", "completed"].includes(hr.status) && (
                  <Button onClick={openEdit} disabled={busy} variant="outline" className="w-full gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="hotel-edit-btn">
                    <FileCheck2 className="w-4 h-4" /> Edit Request
                  </Button>
                )}
                {!["cancelled", "hotel_confirmed", "completed"].includes(hr.status) && (
                  <Button onClick={() => setCancelOpen(true)} disabled={busy} variant="outline" className="w-full gap-2 border-gray-300 text-gray-600 hover:bg-gray-50" data-testid="hotel-cancel-btn">
                    <XCircle className="w-4 h-4" /> Cancel Request
                  </Button>
                )}
                {(hr.status === "hotel_confirmed" || hr.status === "completed") && (
                  <>
                    <Button onClick={downloadVoucher} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="hotel-download-voucher-btn">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4" /> Download Hotel Voucher</>}
                    </Button>
                    {isAdmin && hr.status === "hotel_confirmed" && (
                      <Button onClick={submitComplete} disabled={busy} variant="outline" className="w-full gap-2 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="admin-complete-hotel-btn">
                        <CheckCircle className="w-4 h-4" /> Mark as Completed
                      </Button>
                    )}
                    <div className="text-center text-emerald-700 text-xs font-medium pt-1 flex items-center justify-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5" /> {hr.status === "completed" ? "Trip completed" : "Booking complete"}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader><CardTitle className="text-base">Timeline</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-xs">
                <TimelineDot active label="Request Submitted" date={hr.created_at} color="emerald" />
                <TimelineDot active={!!hr.admin_availability_at} label={hr.status === "not_available" ? "Marked Not Available" : "Marked Available"} date={hr.admin_availability_at} color={hr.status === "not_available" ? "rose" : "emerald"} />
                <TimelineDot active={!!hr.agent_confirmed_at} label="Agent Confirmed Guest" date={hr.agent_confirmed_at} color="violet" />
                <TimelineDot active={!!hr.hotel_confirmed_at} label="Hotel Confirmed" date={hr.hotel_confirmed_at} color="emerald" />
                <TimelineDot active={!!hr.completed_at} label="Marked Completed" date={hr.completed_at} color="blue" />
                {hr.cancelled_at && <TimelineDot active label="Cancelled" date={hr.cancelled_at} color="gray" />}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Modals */}
      <Dialog open={availOpen} onOpenChange={setAvailOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-emerald-700"><CheckCircle className="w-5 h-5" /> Mark Hotel Available</DialogTitle></DialogHeader>
          {pricing && (
            <div className="space-y-4">
              {/* Read-only context from agent's enquiry */}
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-3">
                <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">From Agent&apos;s Enquiry</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Hotel Name <span className="text-gray-400">(editable)</span></Label>
                    <Input
                      value={availForm.hotel_name}
                      onChange={e => setAvailForm({ ...availForm, hotel_name: e.target.value })}
                      placeholder={hr.hotel_name ? hr.hotel_name : "Enter hotel name"}
                      className="h-8 bg-white"
                      data-testid="avail-hotel-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Room Category <span className="text-gray-400">(editable)</span></Label>
                    <Input
                      value={availForm.room_type}
                      onChange={e => setAvailForm({ ...availForm, room_type: e.target.value })}
                      placeholder="e.g. Deluxe Sea View"
                      className="h-8 bg-white"
                      data-testid="avail-room-type"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs pt-1 border-t border-emerald-200/60">
                  <div><p className="text-gray-500">No. of Rooms</p><p className="font-semibold text-gray-900">{pricing.rooms}</p></div>
                  <div><p className="text-gray-500">Meal Plan</p><p className="font-semibold text-gray-900">{hr.meal_plan || "—"}</p></div>
                  <div><p className="text-gray-500">Nights</p><p className="font-semibold text-gray-900">{pricing.nights}N</p></div>
                  <div><p className="text-gray-500">Adults</p><p className="font-semibold text-gray-900">{pricing.adults}</p></div>
                  <div><p className="text-gray-500">Extra Adults</p><p className="font-semibold text-gray-900">{pricing.extraAdults}</p></div>
                  <div><p className="text-gray-500">CWB / CNB</p><p className="font-semibold text-gray-900">{pricing.cwb} / {pricing.cnb}</p></div>
                </div>
              </div>

              {/* Pricing inputs + live computation table */}
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-[10px] uppercase tracking-wide text-gray-500">
                      <th className="px-3 py-2 text-left">Line Item</th>
                      <th className="px-3 py-2 text-right w-32">Rate (INR)</th>
                      <th className="px-3 py-2 text-center w-24">Qty × Nights</th>
                      <th className="px-3 py-2 text-right w-28">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-900">Per Room / Night</td>
                      <td className="px-3 py-2">
                        <Input type="number" min="0" value={availForm.room_rate_per_night} onChange={e => setAvailForm({ ...availForm, room_rate_per_night: e.target.value })} placeholder="0" className="h-8 text-right tabular-nums" data-testid="avail-room-rate" />
                      </td>
                      <td className="px-3 py-2 text-center text-gray-500 text-xs">{pricing.rooms} × {pricing.nights}N</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{pricing.roomTotal > 0 ? `₹${pricing.roomTotal.toLocaleString("en-IN")}` : "—"}</td>
                    </tr>
                    {pricing.extraAdults > 0 && (
                      <tr className="border-t border-gray-100">
                        <td className="px-3 py-2 font-medium text-gray-900">Extra Adult</td>
                        <td className="px-3 py-2">
                          <Input type="number" min="0" value={availForm.extra_adult_rate} onChange={e => setAvailForm({ ...availForm, extra_adult_rate: e.target.value })} placeholder="0" className="h-8 text-right tabular-nums" data-testid="avail-extra-adult-rate" />
                        </td>
                        <td className="px-3 py-2 text-center text-gray-500 text-xs">{pricing.extraAdults} × {pricing.nights}N</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{pricing.extraAdultTotal > 0 ? `₹${pricing.extraAdultTotal.toLocaleString("en-IN")}` : "—"}</td>
                      </tr>
                    )}
                    {pricing.cwb > 0 && (
                      <tr className="border-t border-gray-100">
                        <td className="px-3 py-2 font-medium text-gray-900">CWB <span className="text-xs text-gray-500">(Child w/ bed)</span></td>
                        <td className="px-3 py-2">
                          <Input type="number" min="0" value={availForm.cwb_rate} onChange={e => setAvailForm({ ...availForm, cwb_rate: e.target.value })} placeholder="0" className="h-8 text-right tabular-nums" data-testid="avail-cwb-rate" />
                        </td>
                        <td className="px-3 py-2 text-center text-gray-500 text-xs">{pricing.cwb} × {pricing.nights}N</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{pricing.cwbTotal > 0 ? `₹${pricing.cwbTotal.toLocaleString("en-IN")}` : "—"}</td>
                      </tr>
                    )}
                    {pricing.cnb > 0 && (
                      <tr className="border-t border-gray-100">
                        <td className="px-3 py-2 font-medium text-gray-900">CNB <span className="text-xs text-gray-500">(Child no bed)</span></td>
                        <td className="px-3 py-2">
                          <Input type="number" min="0" value={availForm.cnb_rate} onChange={e => setAvailForm({ ...availForm, cnb_rate: e.target.value })} placeholder="0" className="h-8 text-right tabular-nums" data-testid="avail-cnb-rate" />
                        </td>
                        <td className="px-3 py-2 text-center text-gray-500 text-xs">{pricing.cnb} × {pricing.nights}N</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{pricing.cnbTotal > 0 ? `₹${pricing.cnbTotal.toLocaleString("en-IN")}` : "—"}</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-emerald-50 border-t-2 border-emerald-300">
                    <tr>
                      <td colSpan={3} className="px-3 py-3 text-right font-semibold text-emerald-900 uppercase text-xs tracking-wide">Total Amount</td>
                      <td className="px-3 py-3 text-right tabular-nums font-bold text-emerald-700 text-base" data-testid="avail-total-amount">₹{pricing.total.toLocaleString("en-IN")}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div>
                <Label className="text-sm">Note (optional)</Label>
                <textarea value={availForm.admin_note} onChange={e => setAvailForm({ ...availForm, admin_note: e.target.value })} rows={2} className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm" placeholder="e.g. Pool-view room, complimentary upgrade, special inclusions..." data-testid="avail-note" />
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setAvailOpen(false)} className="flex-1">Cancel</Button>
                <Button onClick={submitAvailable} disabled={busy || pricing.total <= 0} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="avail-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Confirm Available — ₹${pricing.total.toLocaleString("en-IN")}`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={notAvailOpen} onOpenChange={setNotAvailOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-rose-700"><XCircle className="w-5 h-5" /> Mark Not Available</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Reason / Note</Label>
              <textarea value={notAvailForm.admin_note} onChange={e => setNotAvailForm({ admin_note: e.target.value })} rows={3} className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm" placeholder="Sold out, no rooms for these dates..." data-testid="notavail-note" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setNotAvailOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={submitNotAvailable} disabled={busy} className="flex-1 bg-rose-600 hover:bg-rose-700" data-testid="notavail-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Mark Not Available"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={guestOpen} onOpenChange={setGuestOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-emerald-700"><UserCheck className="w-5 h-5" /> Guest Details to Confirm</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Guest Name *</Label>
              <Input value={guestForm.guest_name} onChange={e => setGuestForm({ ...guestForm, guest_name: e.target.value })} data-testid="guest-name" />
            </div>
            <div>
              <Label className="text-sm">Guest Mobile *</Label>
              <Input value={guestForm.guest_mobile} onChange={e => setGuestForm({ ...guestForm, guest_mobile: e.target.value })} placeholder="+91 ..." data-testid="guest-mobile" />
            </div>
            <div>
              <Label className="text-sm">Guest Email</Label>
              <Input type="email" value={guestForm.guest_email} onChange={e => setGuestForm({ ...guestForm, guest_email: e.target.value })} data-testid="guest-email" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setGuestOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={submitGuest} disabled={busy} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="guest-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Booking"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={hotelConfirmOpen} onOpenChange={setHotelConfirmOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-emerald-700"><ClipboardCheck className="w-5 h-5" /> Hotel Confirmation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Hotel Confirmation Number *</Label>
              <Input value={hotelConfirmForm.hotel_confirmation_number} onChange={e => setHotelConfirmForm({ ...hotelConfirmForm, hotel_confirmation_number: e.target.value })} placeholder="e.g. TAJ-INX-2026-7842" data-testid="hotel-confirm-number" />
            </div>
            <div>
              <Label className="text-sm">Note (optional)</Label>
              <textarea value={hotelConfirmForm.admin_note} onChange={e => setHotelConfirmForm({ ...hotelConfirmForm, admin_note: e.target.value })} rows={3} className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm" data-testid="hotel-confirm-note" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setHotelConfirmOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={submitHotelConfirm} disabled={busy} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="hotel-confirm-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Hotel"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="hotel-edit-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-emerald-700"><FileCheck2 className="w-5 h-5" /> Edit Hotel Request</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-sm">Hotel Name <span className="text-gray-400 text-xs">(optional)</span></Label>
              <Input value={editForm.hotel_name || ""} onChange={e => setEditForm({ ...editForm, hotel_name: e.target.value })} data-testid="edit-hotel-name" />
            </div>
            <div>
              <Label className="text-sm">City *</Label>
              <Input value={editForm.city || ""} onChange={e => setEditForm({ ...editForm, city: e.target.value })} data-testid="edit-city" />
            </div>
            <div>
              <Label className="text-sm">Destination / State</Label>
              <Input value={editForm.destination || ""} onChange={e => setEditForm({ ...editForm, destination: e.target.value })} data-testid="edit-destination" />
            </div>
            <div>
              <Label className="text-sm">Check-in *</Label>
              <Input type="date" value={editForm.check_in || ""} onChange={e => setEditForm({ ...editForm, check_in: e.target.value })} data-testid="edit-checkin" />
            </div>
            <div>
              <Label className="text-sm">Check-out *</Label>
              <Input type="date" value={editForm.check_out || ""} onChange={e => setEditForm({ ...editForm, check_out: e.target.value })} data-testid="edit-checkout" />
            </div>
            <div>
              <Label className="text-sm">Rooms</Label>
              <Input type="number" min="1" value={editForm.rooms || 1} onChange={e => setEditForm({ ...editForm, rooms: e.target.value })} data-testid="edit-rooms" />
            </div>
            <div>
              <Label className="text-sm">Room Category <span className="text-gray-400 text-xs">(optional)</span></Label>
              <Input value={editForm.room_type || ""} onChange={e => setEditForm({ ...editForm, room_type: e.target.value })} placeholder="Deluxe / Suite / Standard" data-testid="edit-room-type" />
            </div>
            <div>
              <Label className="text-sm">Meal Plan</Label>
              <select value={editForm.meal_plan || "CP"} onChange={e => setEditForm({ ...editForm, meal_plan: e.target.value })} className="w-full h-10 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="edit-meal-plan">
                <option value="CP">CP (Breakfast)</option>
                <option value="MAP">MAP (B + Dinner)</option>
                <option value="AP">AP (All meals)</option>
                <option value="EP">EP (Room only)</option>
              </select>
            </div>
            <div>
              <Label className="text-sm">Adults</Label>
              <Input type="number" min="1" value={editForm.pax_adults || 1} onChange={e => setEditForm({ ...editForm, pax_adults: e.target.value })} data-testid="edit-adults" />
            </div>
            <div>
              <Label className="text-sm">Children</Label>
              <Input type="number" min="0" value={editForm.pax_children || 0} onChange={e => setEditForm({ ...editForm, pax_children: e.target.value })} data-testid="edit-children" />
            </div>
            <div>
              <Label className="text-sm">CWB</Label>
              <Input type="number" min="0" value={editForm.cwb || 0} onChange={e => setEditForm({ ...editForm, cwb: e.target.value })} data-testid="edit-cwb" />
            </div>
            <div>
              <Label className="text-sm">CNB</Label>
              <Input type="number" min="0" value={editForm.cnb || 0} onChange={e => setEditForm({ ...editForm, cnb: e.target.value })} data-testid="edit-cnb" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm">Special Requests</Label>
              <textarea
                value={editForm.special_requests || ""}
                onChange={e => setEditForm({ ...editForm, special_requests: e.target.value })}
                rows={3}
                className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                data-testid="edit-special"
              />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setEditOpen(false)} className="flex-1">Cancel</Button>
            <Button onClick={submitEdit} disabled={busy} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="edit-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-rose-700"><XCircle className="w-5 h-5" /> Cancel Request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-sm">Reason (optional)</Label>
            <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={3} className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm" data-testid="cancel-reason" />
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setCancelOpen(false)} className="flex-1">Keep</Button>
              <Button onClick={submitCancel} disabled={busy} className="flex-1 bg-rose-600 hover:bg-rose-700" data-testid="cancel-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Cancel"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

const TimelineDot = ({ active, label, date, color }) => {
  const colorMap = {
    emerald: active ? "bg-emerald-500" : "bg-gray-200",
    violet: active ? "bg-violet-500" : "bg-gray-200",
    rose: active ? "bg-rose-500" : "bg-gray-200",
    gray: active ? "bg-gray-500" : "bg-gray-200",
    blue: active ? "bg-blue-500" : "bg-gray-200",
  };
  return (
    <div className="flex items-start gap-3">
      <div className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${colorMap[color]}`} />
      <div className="flex-1">
        <p className={`text-sm ${active ? "text-gray-900 font-medium" : "text-gray-400"}`}>{label}</p>
        {date && <p className="text-[11px] text-gray-500">{new Date(date).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>}
      </div>
    </div>
  );
};
